/**
 * freezeProfiler.ts
 *
 * Wraps the JS Self-Profiling API (`Profiler`/`ProfilerTrace`) to capture the
 * EXACT hot call-stack a real user's main thread was pinned in during a
 * freeze — something a `PerformanceObserver('longtask')` entry alone cannot
 * tell us (it only reports duration, never *what* ran).
 *
 * How it fits with the existing freeze telemetry (`telemetry.ts`,
 * `useFreezeDetector.ts`, and the `PerformanceObserver('longtask')` in
 * `PostEditor.tsx`):
 * - A rolling `Profiler` is started on composer mount and left running for
 *   its whole lifetime, sampling the main-thread stack every ~10ms on a
 *   background mechanism — critically, it keeps sampling EVEN while the
 *   main thread is inside a long synchronous block, which is exactly the
 *   window we can never otherwise see into.
 * - When the existing `longtask` observer fires (i.e. right after the block
 *   ends and the thread frees up), `captureHotStack()` stops that profiler
 *   and reads back its trace, which holds samples taken DURING the just-
 *   finished block, then immediately starts a fresh profiler so the next
 *   freeze is profiled too.
 *
 * Requires the `Document-Policy: js-profiling` response header on the
 * document (see `vite.config.ts`) and only exists in Chromium browsers.
 * Every export here is fully try/catch-guarded end to end: if the header or
 * API is unavailable, everything degrades to a silent no-op and NEVER
 * throws into the UI or into the existing telemetry path.
 */

import { devLog } from '@/utils/logger';

// ---------------------------------------------------------------------------
// Minimal, module-local typings for the JS Self-Profiling API.
// Not (yet) part of TypeScript's lib.dom.d.ts, so we declare only the
// narrow surface we actually use. Kept local to this module (not exported,
// not a global `declare`) so it can never collide with a future lib.dom
// `Profiler` type.
// ---------------------------------------------------------------------------

interface ProfilerInitOptions {
  sampleInterval: number;
  maxBufferSize: number;
}

interface ProfilerFrame {
  name: string;
  resourceId?: number;
  line?: number;
  column?: number;
}

interface ProfilerStack {
  frameId: number;
  parentId?: number;
}

interface ProfilerSample {
  stackId?: number;
  timestamp: number;
}

interface ProfilerTrace {
  resources: string[];
  frames: ProfilerFrame[];
  stacks: ProfilerStack[];
  samples: ProfilerSample[];
}

interface ProfilerInstance extends EventTarget {
  readonly sampleInterval: number;
  readonly stopped: boolean;
  stop(): Promise<ProfilerTrace>;
}

interface ProfilerConstructor {
  new (options: ProfilerInitOptions): ProfilerInstance;
}

interface WindowWithProfiler extends Window {
  Profiler?: ProfilerConstructor;
}

const SAMPLE_INTERVAL_MS = 10;
// ~250k samples at a 10ms interval is ~40 minutes of continuous sampling
// before the buffer fills and we have to recycle the profiler.
const MAX_BUFFER_SIZE = 250_000;

const MAX_STACK_FRAMES = 15;
const MAX_SUMMARY_CHARS = 4000;
const TOP_LEAF_FRAME_COUNT = 3;

/** The currently-running rolling profiler, if any. Module-scoped singleton. */
let activeProfiler: ProfilerInstance | undefined;

const getProfilerConstructor = (): ProfilerConstructor | undefined => {
  try {
    const ctor = (window as WindowWithProfiler).Profiler;
    return typeof ctor === 'function' ? ctor : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Whether the JS Self-Profiling API is available in this browser/document.
 * False in non-Chromium browsers, or if the `Document-Policy: js-profiling`
 * response header is missing from the document.
 */
export const isFreezeProfilingSupported = (): boolean => {
  try {
    return typeof (window as WindowWithProfiler).Profiler === 'function';
  } catch {
    return false;
  }
};

/**
 * Start (or recycle) the rolling background profiler. No-op if the API is
 * unsupported. Safe to call multiple times — a pre-existing profiler is
 * left running rather than duplicated.
 */
export const startRollingProfiler = (): void => {
  try {
    if (activeProfiler) {
      return;
    }

    const ProfilerCtor = getProfilerConstructor();
    if (!ProfilerCtor) {
      return;
    }

    const profiler = new ProfilerCtor({
      sampleInterval: SAMPLE_INTERVAL_MS,
      maxBufferSize: MAX_BUFFER_SIZE,
    });
    activeProfiler = profiler;

    // The profiler stops sampling on its own once `maxBufferSize` is
    // reached (firing 'samplebufferfull'). Recycle it so profiling never
    // silently goes dark for the rest of the composer session.
    profiler.addEventListener('samplebufferfull', () => {
      try {
        // Only recycle if this handler's profiler is still the active one
        // (avoids racing a concurrent captureHotStack()/stopProfiler() call).
        if (activeProfiler !== profiler) {
          return;
        }
        activeProfiler = undefined;
        profiler.stop().catch(() => {
          // Discard — we only needed the buffer cleared, not the trace.
        });
        startRollingProfiler();
      } catch {
        // Recycling must never throw; profiling simply stays off until the
        // next captureHotStack()/mount cycle restarts it.
      }
    });
  } catch (error) {
    devLog.warn('[FreezeProfiler] failed to start rolling profiler (non-fatal):', error);
    activeProfiler = undefined;
  }
};

/**
 * Stop the rolling profiler and clear it (component unmount). Discards the
 * trace — this is teardown, not a capture. Wrapped; never throws.
 */
export const stopProfiler = (): void => {
  try {
    const profiler = activeProfiler;
    activeProfiler = undefined;
    if (profiler) {
      profiler.stop().catch(() => {
        // Teardown must never throw/reject into the caller.
      });
    }
  } catch {
    // no-op
  }
};

/** Resolve a frame to a compact, readable `name (resource:line:col)` string. */
const formatFrame = (trace: ProfilerTrace, frameId: number): string => {
  const frame = trace.frames[frameId];
  if (!frame) {
    return `<unknown frame ${frameId}>`;
  }

  const name = frame.name && frame.name.length > 0 ? frame.name : '(anonymous)';
  const resource = frame.resourceId !== undefined ? trace.resources[frame.resourceId] : undefined;

  if (!resource) {
    return name;
  }

  const line = frame.line ?? '?';
  const column = frame.column ?? '?';
  return `${name} (${resource}:${line}:${column})`;
};

/** Count how many samples landed on each stackId. */
const countSamplesByStackId = (samples: ProfilerSample[]): Map<number, number> => {
  const counts = new Map<number, number>();
  for (const sample of samples) {
    if (sample.stackId === undefined) {
      continue;
    }
    counts.set(sample.stackId, (counts.get(sample.stackId) ?? 0) + 1);
  }
  return counts;
};

/** Find the stackId with the most samples (the leaf the thread sat in most). */
const findHottestStackId = (stackCounts: Map<number, number>): { stackId: number; count: number } | null => {
  let hottestStackId: number | undefined;
  let hottestCount = -1;

  for (const [stackId, count] of stackCounts) {
    if (count > hottestCount) {
      hottestCount = count;
      hottestStackId = stackId;
    }
  }

  return hottestStackId !== undefined ? { stackId: hottestStackId, count: hottestCount } : null;
};

/**
 * Walk `stacks[stackId].parentId` up to the root, mapping each frame to a
 * readable string. Returns leaf-first (index 0 = innermost frame), capped
 * to `MAX_STACK_FRAMES`. Cycle-guarded in case of malformed trace data.
 */
const walkStackChain = (trace: ProfilerTrace, stackId: number): string[] => {
  const chain: string[] = [];
  const visited = new Set<number>();
  let currentId: number | undefined = stackId;

  while (currentId !== undefined && chain.length < MAX_STACK_FRAMES) {
    if (visited.has(currentId)) {
      break; // defensive: malformed/cyclic parentId chain
    }
    visited.add(currentId);

    const stackEntry: ProfilerStack | undefined = trace.stacks[currentId];
    if (!stackEntry) {
      break;
    }

    chain.push(formatFrame(trace, stackEntry.frameId));
    currentId = stackEntry.parentId;
  }

  return chain;
};

/**
 * Aggregate self-sample counts per LEAF frame (not per full stack path) and
 * return the top N hottest — usually the smoking gun for a freeze.
 */
const findTopLeafFrames = (
  trace: ProfilerTrace,
  stackCounts: Map<number, number>,
  topN: number
): Array<{ frameId: number; count: number }> => {
  const leafCounts = new Map<number, number>();

  for (const [stackId, count] of stackCounts) {
    const stackEntry = trace.stacks[stackId];
    if (!stackEntry) {
      continue;
    }
    leafCounts.set(stackEntry.frameId, (leafCounts.get(stackEntry.frameId) ?? 0) + count);
  }

  return Array.from(leafCounts.entries())
    .map(([frameId, count]) => ({ frameId, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
};

/** Build the compact hot-stack summary string from a stopped trace. */
const buildHotStackSummary = (trace: ProfilerTrace): string | null => {
  if (!trace.samples || trace.samples.length === 0) {
    return null;
  }

  const stackCounts = countSamplesByStackId(trace.samples);
  if (stackCounts.size === 0) {
    return null;
  }

  const hottest = findHottestStackId(stackCounts);
  if (!hottest) {
    return null;
  }

  const topLeaves = findTopLeafFrames(trace, stackCounts, TOP_LEAF_FRAME_COUNT);
  const summaryLine =
    topLeaves.length > 0
      ? `Top hot frames: ${topLeaves.map((leaf) => `${formatFrame(trace, leaf.frameId)} x${leaf.count}`).join(', ')}`
      : '';

  const chain = walkStackChain(trace, hottest.stackId);
  const stackLine = chain.length > 0 ? `Hottest stack (${hottest.count} samples): ${chain.join(' < ')}` : '';

  const combined = [summaryLine, stackLine].filter((line) => line.length > 0).join('\n');
  if (combined.length === 0) {
    return null;
  }

  return combined.length > MAX_SUMMARY_CHARS ? combined.slice(0, MAX_SUMMARY_CHARS) : combined;
};

/**
 * Stop the current rolling profiler, read back its trace, compute the
 * hottest call-stack from it, then immediately start a fresh profiler so
 * subsequent freezes keep getting profiled.
 *
 * Returns `null` (never throws/rejects) if profiling is unsupported, no
 * profiler is running, the trace has no samples, or anything goes wrong
 * while reading it.
 */
export const captureHotStack = async (): Promise<string | null> => {
  const profiler = activeProfiler;
  if (!profiler) {
    return null;
  }

  activeProfiler = undefined;

  let trace: ProfilerTrace | null = null;
  try {
    trace = await profiler.stop();
  } catch (error) {
    devLog.warn('[FreezeProfiler] failed to stop profiler for capture (non-fatal):', error);
    trace = null;
  } finally {
    // Always restart, whether the capture succeeded or not, so a repeated
    // freeze later in the session is profiled too.
    startRollingProfiler();
  }

  if (!trace) {
    return null;
  }

  try {
    return buildHotStackSummary(trace);
  } catch (error) {
    devLog.warn('[FreezeProfiler] failed to summarize trace (non-fatal):', error);
    return null;
  }
};
